/**
 * @file Tree 树形列表
 * @description 可展开节点；支持多选勾选与单选高亮。
 *
 * @whenToUse 目录、组织架构、权限树。
 * @whenNotToUse 下拉选树节点（用 TreeSelect）。
 */

import { useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { Checkbox } from '@/components/ui/checkbox.tsx'

export type TreeNode = {
  key: string
  title: ReactNode
  children?: TreeNode[]
  disabled?: boolean
}

export type TreeProps = {
  treeData: TreeNode[]
  /** 展开的 key */
  expandedKeys?: string[]
  onExpand?: (keys: string[]) => void
  /** 默认展开 */
  defaultExpandedKeys?: string[]
  /** 勾选的 key（多选） */
  checkedKeys?: string[]
  onCheck?: (keys: string[]) => void
  /** 是否显示勾选 */
  checkable?: boolean
  /** 高亮选中的 key（单选回显，如 TreeSelect） */
  selectedKeys?: string[]
  /** 点击节点标题/行 */
  onSelect?: (key: string, node: TreeNode) => void
  /** 仅叶子节点可选（有 children 的只展开） */
  selectableLeafOnly?: boolean
  className?: string
}

function collectChildKeys(node: TreeNode): string[] {
  const keys: string[] = []
  const walk = (n: TreeNode) => {
    keys.push(n.key)
    n.children?.forEach(walk)
  }
  walk(node)
  return keys
}

export function Tree({
  treeData,
  expandedKeys,
  onExpand,
  defaultExpandedKeys = [],
  checkedKeys = [],
  onCheck,
  checkable = false,
  selectedKeys = [],
  onSelect,
  selectableLeafOnly = false,
  className,
}: TreeProps) {
  const [innerExpanded, setInnerExpanded] = useState(defaultExpandedKeys)
  const expanded = expandedKeys ?? innerExpanded

  const setExpanded = (keys: string[]) => {
    onExpand?.(keys)
    if (expandedKeys === undefined) setInnerExpanded(keys)
  }

  const toggleExpand = (key: string) => {
    setExpanded(
      expanded.includes(key)
        ? expanded.filter((k) => k !== key)
        : [...expanded, key],
    )
  }

  const toggleCheck = (node: TreeNode, checked: boolean) => {
    const related = collectChildKeys(node)
    if (checked) {
      onCheck?.([...new Set([...checkedKeys, ...related])])
      return
    }
    onCheck?.(checkedKeys.filter((k) => !related.includes(k)))
  }

  const handleSelect = (node: TreeNode, hasChildren: boolean) => {
    if (node.disabled) return
    if (selectableLeafOnly && hasChildren) return
    onSelect?.(node.key, node)
  }

  const renderNodes = (nodes: TreeNode[], depth: number) =>
    nodes.map((node) => {
      const hasChildren = Boolean(node.children?.length)
      const isExpanded = expanded.includes(node.key)
      const isChecked = checkedKeys.includes(node.key)
      const isSelected = selectedKeys.includes(node.key)
      const canSelect =
        Boolean(onSelect) &&
        !node.disabled &&
        !(selectableLeafOnly && hasChildren)

      return (
        <li key={node.key} className="select-none">
          <div
            role={canSelect ? 'treeitem' : undefined}
            aria-selected={canSelect ? isSelected : undefined}
            className={cn(
              'flex h-9 items-center gap-1 rounded-md px-1 text-sm transition-opacity duration-150',
              canSelect && 'cursor-pointer hover:opacity-90',
              isSelected && 'bg-primary/15 text-primary',
              !isSelected && 'text-foreground',
              node.disabled && 'opacity-50',
            )}
            style={{ paddingLeft: depth * 16 + 4 }}
            onClick={() => {
              if (canSelect) handleSelect(node, hasChildren)
            }}
          >
            {hasChildren ? (
              <button
                type="button"
                aria-label={isExpanded ? '收起' : '展开'}
                className="inline-flex size-6 items-center justify-center rounded-md text-muted"
                onClick={(event) => {
                  event.stopPropagation()
                  toggleExpand(node.key)
                }}
              >
                <ChevronRight
                  className={cn(
                    'size-4 transition-transform duration-200',
                    isExpanded && 'rotate-90',
                  )}
                  aria-hidden
                />
              </button>
            ) : (
              <span className="inline-flex size-6" aria-hidden />
            )}
            {checkable ? (
              <Checkbox
                checked={isChecked}
                disabled={node.disabled}
                aria-label={typeof node.title === 'string' ? node.title : '选择'}
                onChange={(event) => toggleCheck(node, event.target.checked)}
                onClick={(event) => event.stopPropagation()}
              />
            ) : null}
            <span className="min-w-0 flex-1 truncate">{node.title}</span>
          </div>
          {hasChildren && isExpanded ? (
            <ul>{renderNodes(node.children!, depth + 1)}</ul>
          ) : null}
        </li>
      )
    })

  return (
    <ul className={cn('text-sm', className)} role="tree">
      {renderNodes(treeData, 0)}
    </ul>
  )
}
